import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Search, X } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { motion } from 'motion/react';
import { useSession } from '@/features/auth';
import { Input } from '@/shared/ui/Input';
import { Button } from '@/shared/ui/Button';
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogDescription,
} from '@/shared/ui/Dialog';
import { cn } from '@/shared/lib/utils';
import type { GlobalSearchActionHit, GlobalSearchHit, GlobalSearchRecentHit } from '../domain/global-search.types';
import { parseSearchCommandQuery, searchModePlaceholder } from '../domain/parse-search-command';
import { useGlobalSearch } from '../hooks/use-global-search';
import { useGlobalSearchRecents } from '../hooks/use-global-search-recents';
import { GlobalSearchResultsBody } from './GlobalSearchResultsBody';

function rememberFromHit(
  hit: GlobalSearchHit,
  pushRecent: ReturnType<typeof useGlobalSearchRecents>['pushRecent'],
): void {
  if (hit.kind === 'patient') {
    pushRecent({ id: hit.id, kind: 'patient', label: hit.name, href: hit.href });
    return;
  }
  if (hit.kind === 'appointment') {
    pushRecent({ id: hit.id, kind: 'appointment', label: hit.patientName, href: hit.href });
    return;
  }
  if (hit.kind === 'invoice') {
    pushRecent({ id: hit.id, kind: 'invoice', label: hit.displayNumber, href: hit.href });
    return;
  }
  if (hit.kind === 'recent') {
    pushRecent({
      id: hit.entityId,
      kind: hit.entityKind,
      label: hit.label,
      href: hit.href,
    });
  }
}

export interface GlobalSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Spotlight-style command palette for clinic-wide search and commands.
 */
export function GlobalSearchDialog({ open, onOpenChange }: GlobalSearchDialogProps): JSX.Element {
  const navigate = useNavigate();
  const { user } = useSession();
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [focused, setFocused] = useState(false);

  const { recents, pushRecent, clearRecents } = useGlobalSearchRecents();
  const search = useGlobalSearch(query, open, recents, user?.role);
  const {
    hits,
    patientHits,
    appointmentHits,
    invoiceHits,
    isIdle,
    isBelowMinChars,
    isFetching,
    isError,
    hasResults,
    debounced,
    mode,
    searchTerm,
  } = search;

  const parsed = parseSearchCommandQuery(query);
  const placeholder = searchModePlaceholder(parsed.mode);
  const isCommandMode = mode === 'command';

  useEffect(() => {
    if (!open) {
      setQuery('');
      setHighlight(0);
      setFocused(false);
      return;
    }
    const id = window.setTimeout(() => inputRef.current?.focus(), 10);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    setHighlight(0);
  }, [debounced, hits.length, isIdle, mode]);

  const selectHref = useCallback(
    (href: string, hit?: GlobalSearchHit) => {
      if (hit) {
        rememberFromHit(hit, pushRecent);
      } else {
        const matched = hits.find((h) => 'href' in h && h.href === href);
        if (matched) rememberFromHit(matched, pushRecent);
      }
      onOpenChange(false);
      setQuery('');
      navigate(href);
    },
    [hits, navigate, onOpenChange, pushRecent],
  );

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(hits.length - 1, 0)));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
      return;
    }
    if (event.key === 'Enter') {
      const chosen = hits[highlight];
      if (chosen && 'href' in chosen) {
        event.preventDefault();
        selectHref(chosen.href, chosen);
      }
    }
  };

  const showLoading =
    !isIdle &&
    !isCommandMode &&
    !isBelowMinChars &&
    isFetching &&
    !hasResults &&
    searchTerm.length >= 2;
  const showNoResults =
    !isIdle &&
    !isBelowMinChars &&
    !isFetching &&
    !isError &&
    !hasResults &&
    (isCommandMode || searchTerm.length >= 2) &&
    query.trim().length > 0;
  const waitingForDebounce =
    !isIdle &&
    !isCommandMode &&
    !isBelowMinChars &&
    parsed.term.length >= 2 &&
    debounced !== parsed.term &&
    !hasResults;

  const idleRecents = hits.filter((h): h is GlobalSearchRecentHit => h.kind === 'recent');
  const idleActions = hits.filter((h): h is GlobalSearchActionHit => h.kind === 'action');
  const activeDescendant = hits.length > 0 ? `global-search-hit-${highlight}` : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="global-search-overlay backdrop-blur-md" />
        <DialogPrimitive.Content
          className={cn(
            'global-search-palette fixed left-[50%] top-[12%] z-50 w-[min(100%-1.25rem,40rem)] translate-x-[-50%] translate-y-0',
            'sm:w-[min(100%-2rem,44rem)] md:w-[min(92vw,52rem)]',
            'overflow-hidden rounded-[20px] border border-surface-200/80 bg-[var(--bg-surface)] p-0',
            'focus:outline-none',
          )}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: -12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.16, ease: [0.16, 1, 0.3, 1] }}
          >
            <DialogTitle className="sr-only">Global search</DialogTitle>
            <DialogDescription className="sr-only">
              Search patients, appointments, and invoices, or run a command
            </DialogDescription>

            <div
              className={cn(
                'global-search-shell border-b border-surface-100',
                (focused || open) && 'global-search-shell--focus',
              )}
            >
              <div className="pointer-events-none absolute inset-y-0 left-5 flex items-center">
                <Search
                  className={cn('size-5', focused ? 'text-brand-600' : 'text-ink-400')}
                  aria-hidden
                />
              </div>
              <Input
                ref={inputRef}
                type="text"
                role="combobox"
                aria-expanded={open}
                aria-controls={listId}
                aria-autocomplete="list"
                aria-activedescendant={activeDescendant}
                autoComplete="off"
                placeholder={placeholder}
                className="h-16 border-0 bg-transparent pl-14 pr-24 text-base shadow-none placeholder:text-ink-400 focus-visible:ring-0 sm:text-[17px]"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onKeyDown={onKeyDown}
              />
              <div className="absolute inset-y-0 right-4 flex items-center gap-1.5">
                {isFetching && searchTerm.length >= 2 ? (
                  <Loader2 className="size-4 animate-spin text-brand-500" aria-hidden />
                ) : query.length > 0 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-8 text-ink-400 hover:text-ink-700"
                    aria-label="Clear search"
                    onClick={() => {
                      setQuery('');
                      inputRef.current?.focus();
                    }}
                  >
                    <X className="size-3.5" aria-hidden />
                  </Button>
                ) : (
                  <kbd className="rounded-md border border-surface-200 bg-surface-50 px-2 py-1 font-mono text-[11px] text-ink-500">
                    esc
                  </kbd>
                )}
              </div>
            </div>

            <GlobalSearchResultsBody
              isIdle={isIdle}
              isBelowMinChars={isBelowMinChars}
              idleRecents={idleRecents}
              idleActions={idleActions}
              highlight={highlight}
              onHighlight={setHighlight}
              onSelect={selectHref}
              onClearRecents={clearRecents}
              listId={listId}
              showLoading={showLoading}
              waitingForDebounce={waitingForDebounce}
              isError={isError}
              showNoResults={showNoResults}
              debounced={searchTerm}
              query={isCommandMode ? query : searchTerm || query}
              patientHits={patientHits}
              appointmentHits={appointmentHits}
              invoiceHits={invoiceHits}
              hits={hits}
            />

            <div className="flex items-center justify-between gap-3 border-t border-surface-100 bg-surface-50/80 px-4 py-2.5 text-[11px] text-ink-400">
              <div className="flex items-center gap-4">
                <span>
                  <kbd className="rounded border border-surface-200 bg-white px-1.5 py-0.5 font-mono text-[10px] text-ink-500">
                    ↑↓
                  </kbd>{' '}
                  navigate
                </span>
                <span>
                  <kbd className="rounded border border-surface-200 bg-white px-1.5 py-0.5 font-mono text-[10px] text-ink-500">
                    ↵
                  </kbd>{' '}
                  open
                </span>
                <span>
                  <kbd className="rounded border border-surface-200 bg-white px-1.5 py-0.5 font-mono text-[10px] text-ink-500">
                    esc
                  </kbd>{' '}
                  close
                </span>
              </div>
              <span className="hidden text-[10px] text-ink-400 sm:inline">
                {mode === 'all' ? 'p · a · i · > modes' : `Mode: ${mode}`}
              </span>
            </div>
          </motion.div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
